/**
 * Supervision — the surface the no-show pass and the reassign endpoint were
 * missing.
 *
 * runNoShowPass (apps/api/src/operations/lateness.ts) already notices a
 * planned shift nobody clocked in for and tells the owner; reassignOperation
 * (apps/api/src/operations/reassign.ts) already moves a task to someone else.
 * What neither had was a place on the screen: the alert arrived as a
 * notification that opened nothing, and the reassign endpoint had no caller.
 * This file is that place — the alert, and the one-tap answer to it.
 *
 * Everything here is behind supervision_v1, the same flag the detection and
 * the endpoint already live behind. The owner decides; nothing in this file
 * moves work on its own — per the composer doc's rule that reassignment is
 * never automatic.
 *
 * A no-show is answered with cover, not a lecture: the other staff are right
 * there on the card, and one tap hands the shift to one of them. The PATCH
 * contract has no staff_user_id (a shift cannot be edited onto another
 * person), so cover is create-the-cover-then-cancel-the-original — cancel
 * being the soft delete with a reason that keeps the morning readable later
 * (scheduled-shifts.ts). Create first: a failed create leaves the plan
 * untouched, while cancel-first could strand the day with no shift at all.
 */
import { Icon } from '@iconify/react';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { ApiError, type StaffRow, api } from '../api';
import { Avatar } from '../components/Avatar';
import { getLang, t } from '../i18n';
import { get, invalidate } from '../query';

/** One adherence row — the server's shape, declared locally the way
 *  OwnerPulse declares its PlannedShift. */
export interface AdherenceShift {
  id: string;
  staff_user_id: string;
  full_name?: string;
  starts_at: string;
  ends_at: string;
  role_text: string | null;
  location_text: string | null;
  required: boolean;
  on_call: boolean;
  notes: string | null;
  status: 'on_time' | 'late' | 'absent' | 'early_departure' | 'worked_unscheduled' | 'upcoming';
}

/** Self-contained flag read — the same convention DailyReportEntry and every
 *  other flag-gated widget already follow: /v1/flags, never a prop threaded
 *  down from whichever screen happens to host the card. */
export function useSupervisionFlag(): boolean {
  const flagsQ = useQuery({
    queryKey: ['/v1/flags'],
    queryFn: () => api<{ flags: Record<string, boolean> }>('/v1/flags'),
  });
  return flagsQ.data?.flags.supervision_v1 === true;
}

/** Midnight UTC of today/tomorrow — OwnerPulse's dayStart convention, so the
 *  alert and the plan screen agree about which shifts are "today's". */
function dayStart(offset: number): Date {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() + offset);
  return d;
}

/** Today's absent shifts: planned, required or not, past the grace window
 *  with no clock-in. Empty when the flag is off (no fetch at all), when
 *  adherence 403s (a viewer without assign_work), or when everybody came in. */
export function useTodayNoShows(enabled: boolean): AdherenceShift[] {
  const key = useMemo(() => {
    const from = dayStart(0).toISOString();
    const to = dayStart(1).toISOString();
    return `/v1/scheduled-shifts/adherence?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
  }, []);
  const q = useQuery({
    queryKey: [key],
    queryFn: get<{ items: AdherenceShift[] }>,
    enabled,
    refetchInterval: 60_000,
    // A 403 is a viewer without assign_work, not a broken screen — the alert
    // simply isn't theirs to see.
    retry: false,
  });
  if (!enabled || q.isError) return [];
  return (q.data?.items ?? []).filter((i) => i.status === 'absent');
}

function timeLabel(iso: string): string {
  try {
    const lang = getLang();
    const locale =
      lang === 'he' ? 'he-IL' : lang === 'ur' ? 'ur-PK' : lang === 'tl' ? 'fil-PH' : 'en-US';
    return new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

/**
 * The people a card offers as the answer — the household's active staff
 * minus whoever the work sits with now. Tapping one is the whole action:
 * the owner already knows who should pick it up, and a confirm dialog would
 * be the app asking him to say it twice. The busy chip disables the rest so
 * a double-tap cannot hand the same morning to two people.
 */
export function ReassignChips({
  people,
  busyId,
  doneId,
  onPick,
}: {
  people: StaffRow[];
  busyId: string | null;
  doneId?: string | null;
  onPick: (person: StaffRow) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {people.map((person) => (
        <button
          key={person.id}
          type="button"
          disabled={busyId !== null}
          onClick={() => onPick(person)}
          className={`flex h-11 items-center gap-2 rounded-full border py-1 pe-4 ps-1 transition-all active:scale-95 disabled:opacity-50 ${
            doneId === person.id
              ? 'border-wealth/30 bg-wealth/10 text-wealth'
              : 'border-border bg-card text-foreground shadow-sm'
          }`}
        >
          <Avatar
            name={person.full_name}
            userId={person.id}
            size="xs"
            className="border border-black/[0.05]"
          />
          <span className="text-[12px] font-bold tracking-tight">{person.full_name}</span>
          {busyId === person.id && (
            <Icon icon="ph:circle-notch-bold" className="animate-spin text-sm" aria-hidden />
          )}
          {doneId === person.id && <Icon icon="ph:check-bold" className="text-sm" aria-hidden />}
        </button>
      ))}
    </div>
  );
}

/**
 * One collapsed morning: who didn't come in, when they were supposed to, and
 * the one-tap cover beside it.
 */
export function NoShowCard({
  shift,
  staff,
  onChange,
}: {
  shift: AdherenceShift;
  staff: StaffRow[];
  onChange: () => void;
}) {
  const person = staff.find((s) => s.id === shift.staff_user_id);
  const cover = staff.filter((s) => !s.disabled_at && s.id !== shift.staff_user_id);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [doneId, setDoneId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function coverWith(next: StaffRow) {
    setBusyId(next.id);
    setErr(null);
    try {
      await api('/v1/scheduled-shifts', {
        method: 'POST',
        body: JSON.stringify({
          staff_user_id: next.id,
          starts_at: shift.starts_at,
          ends_at: shift.ends_at,
          ...(shift.role_text ? { role_text: shift.role_text } : {}),
          ...(shift.location_text ? { location_text: shift.location_text } : {}),
          required: shift.required,
          on_call: shift.on_call,
          ...(shift.notes ? { notes: shift.notes } : {}),
        }),
      });
      await api(`/v1/scheduled-shifts/${shift.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          cancel: true,
          cancel_reason: `No-show; covered by ${next.full_name}`.slice(0, 280),
        }),
      });
      setDoneId(next.id);
      invalidate('/v1/scheduled-shifts', '/v1/notifications');
      onChange();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t('Failed'));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="flex flex-col gap-5 rounded-[24px] border border-border bg-card p-6 shadow-[0_8px_30px_rgba(0,0,0,0.02)]">
      <div className="flex items-center gap-2.5">
        <span className="flex h-8 w-8 items-center justify-center rounded-2xl bg-urgent/10 text-urgent">
          <Icon icon="ph:user-minus-bold" className="text-lg" aria-hidden />
        </span>
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-urgent">
          {person
            ? t('{{name}} didn’t clock in').replace('{{name}}', person.full_name)
            : t('Nobody clocked in')}
        </span>
      </div>
      <div className="flex items-center justify-between gap-3">
        <h3
          className="text-[18px] font-semibold leading-[1.3] tracking-tight text-foreground"
          dir="auto"
        >
          {shift.role_text ?? t('Planned shift')}
        </h3>
        <span className="shrink-0 text-[12px] font-bold tabular-nums tracking-tight text-muted-foreground">
          {timeLabel(shift.starts_at)}–{timeLabel(shift.ends_at)}
        </span>
      </div>

      {doneId === null ? (
        cover.length > 0 && (
          <div className="flex flex-col gap-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
              {t('Cover with')}
            </p>
            <ReassignChips people={cover} busyId={busyId} onPick={(p) => void coverWith(p)} />
          </div>
        )
      ) : (
        <p className="text-xs font-semibold text-wealth">
          {t('{{name}} has the shift now.').replace(
            '{{name}}',
            cover.find((p) => p.id === doneId)?.full_name ?? '',
          )}
        </p>
      )}
      {err && <p className="text-xs font-bold text-urgent">{err}</p>}
    </div>
  );
}
